import {
  BracketCellForm,
  IMatch,
  PlayValues,
} from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import { buildBracketCellReorderPayloads } from './bracketCellReorder.util';
import {
  anchorMatchHasChanges,
  bracketCellFormFieldsEqual,
  bracketCellMetadataPatchChanged,
} from './bracketCellPatchDiff.util';
import { mapMatchToBracketCellPatchPayload } from './playTagTypeConverter';
import { groupMatchesByBracketCell } from './groupMatchesByBracketCell';

export type TournamentBracketCellClassPlayPatch = {
  id: string;
  matches?: Record<string, unknown>[];
  deletedMatchIds?: number[];
  bracketCell?: Record<string, unknown>;
};

function mapBracketCellCreatePayload(match: IMatch): Record<string, unknown> {
  return mapMatchToBracketCellPatchPayload(match);
}

function coerceTeamId(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === '' || value === null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function deriveLoserTeamId(
  winnerTeamId: number,
  primary?: IMatch,
): number | null | undefined {
  const homeId = primary?.homeTeam?.id;
  const awayId = primary?.awayTeam?.id;
  if (winnerTeamId === homeId && awayId != null) {
    return awayId;
  }
  if (winnerTeamId === awayId && homeId != null) {
    return homeId;
  }
  return undefined;
}


function layoutValueToBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return value === 'true';
}

function buildBracketCellPatch(
  cellForm: BracketCellForm | undefined,
  matchCount: number,
  primary?: IMatch,
  isNewCell?: boolean,
): Record<string, unknown> | undefined {
  const patch: Record<string, unknown> = {};

  if (cellForm) {
    if (cellForm.winnerTeamId !== undefined && cellForm.winnerTeamId !== '') {
      patch.winnerTeamId = coerceTeamId(cellForm.winnerTeamId);
    }

    if (cellForm.loserTeamId !== undefined && cellForm.loserTeamId !== '') {
      patch.loserTeamId = coerceTeamId(cellForm.loserTeamId);
    }

    if (
      typeof patch.winnerTeamId === 'number' &&
      patch.loserTeamId === undefined
    ) {
      const derived = deriveLoserTeamId(patch.winnerTeamId, primary);
      if (derived != null) {
        patch.loserTeamId = derived;
      }
    }

    if (cellForm.progressionMode !== undefined && cellForm.progressionMode !== '') {
      patch.progressionMode = Number(cellForm.progressionMode);
    }
    if (cellForm.status !== undefined && cellForm.status !== '') {
      patch.status = Number(cellForm.status);
    }
    if (cellForm.progressedAt !== undefined) {
      patch.progressedAt = cellForm.progressedAt;
    }
    if (cellForm.progressionExplanation !== undefined) {
      patch.progressionExplanation = cellForm.progressionExplanation;
    }
  }

  if (matchCount > 0) {
    patch.matchCount = matchCount;
  }

  const hasFormRound = cellForm?.round !== undefined && cellForm.round !== '';
  const hasFormIsGold = cellForm?.isGold !== undefined;
  const hasFormBracket = cellForm?.bracket !== undefined && cellForm.bracket !== '';

  if (hasFormRound) {
    patch.round = Number(cellForm!.round);
  } else if (isNewCell && primary?.type !== undefined) {
    patch.round = primary.type ?? null;
  }

  if (hasFormIsGold) {
    patch.isGold = layoutValueToBoolean(cellForm!.isGold);
  } else if (isNewCell && primary) {
    patch.isGold = layoutValueToBoolean(primary.isGold);
  }

  if (hasFormBracket) {
    patch.bracket = Number(cellForm!.bracket);
  } else if (isNewCell && primary) {
    patch.bracket = Number(primary.bracket ?? 0);
  }

  if (isNewCell && primary) {
    patch.sortKey = primary.sortKey ?? null;
    if (primary.tournamentGroupId != null) {
      patch.tournamentGroupId = primary.tournamentGroupId;
    }
  }

  return Object.keys(patch).length > 0 ? patch : undefined;
}

function isUnpersistedBracketCell(cellId: string, values: PlayValues): boolean {
  const initialIds = values.initialBracketCellIds;
  if (initialIds) {
    return !initialIds.includes(cellId);
  }
  return values.bracketCells[cellId]?.isNewCell === true;
}

function hasPendingCreateMatches(matches: IMatch[]): boolean {
  return matches.some((m) => typeof m.id === 'string');
}

function hasLayoutMetadataPatch(
  bracketCellPatch: Record<string, unknown> | undefined,
): boolean {
  if (!bracketCellPatch) {
    return false;
  }
  return (
    'round' in bracketCellPatch ||
    'isGold' in bracketCellPatch ||
    'bracket' in bracketCellPatch
  );
}

function findBaselineAnchorMatch(
  baseline: PlayValues | undefined,
  cellId: string,
  anchorId: number | string | undefined,
): IMatch | undefined {
  if (!baseline || anchorId == null) {
    return undefined;
  }
  return Object.values(baseline.tournamentMatches).find(
    (m) => m.bracketCellId === cellId && m.id === anchorId,
  );
}

function hasPlaceholderOnAnchor(anchor?: IMatch): boolean {
  return Boolean(anchor?.homeTeamPlaceholder || anchor?.awayTeamPlaceholder);
}

export function buildTournamentBracketCellsForClassPlay(
  values: PlayValues,
  baseline?: PlayValues,
): TournamentBracketCellClassPlayPatch[] {
  const pendingDeletedCells = new Set(values.pendingDeletedBracketCellIds ?? []);
  const groups = groupMatchesByBracketCell(Object.values(values.tournamentMatches));
  const patches: TournamentBracketCellClassPlayPatch[] = [];

  for (const g of groups) {
    if (g.isLegacySingle || pendingDeletedCells.has(g.cellId)) {
      continue;
    }
    const primary = g.matches[0];
    const cellCount = Math.max(
      primary.bracketCellMatchCount ?? 0,
      g.matches.length,
    );
    const isGroupCell = primary.tournamentGroupId != null;
    const pendingDeletes = values.bracketCells[g.cellId]?.pendingDeletedMatchIds ?? [];
    const isUnpersisted = isUnpersistedBracketCell(g.cellId, values);
    const hasCreates = hasPendingCreateMatches(g.matches);
    const isNewCell =
      isUnpersisted || values.bracketCells[g.cellId]?.isNewCell === true;
    const cellForm = values.bracketCells[g.cellId];
    const baselineCellForm = baseline?.bracketCells[g.cellId];
    const baselineAnchor = findBaselineAnchorMatch(baseline, g.cellId, primary.id);
    const bracketCellPatch = buildBracketCellPatch(
      cellForm,
      g.matches.length,
      primary,
      isNewCell,
    );
    const baselineBracketCellPatch = baseline
      ? buildBracketCellPatch(
          baselineCellForm,
          g.matches.length,
          baselineAnchor,
          false,
        )
      : undefined;
    const anchorWouldChange = anchorMatchHasChanges(
      primary,
      baselineAnchor,
      bracketCellPatch,
      baselineBracketCellPatch,
    );
    const hasMetadataPatch = baseline
      ? bracketCellMetadataPatchChanged(bracketCellPatch, baselineBracketCellPatch)
      : hasLayoutMetadataPatch(bracketCellPatch);
    if (
      !isUnpersisted &&
      !hasCreates &&
      baseline &&
      !isNewCell &&
      cellCount <= 1 &&
      !isGroupCell &&
      pendingDeletes.length === 0 &&
      !hasMetadataPatch &&
      bracketCellFormFieldsEqual(cellForm, baselineCellForm) &&
      !anchorWouldChange
    ) {
      continue;
    }

    const sortedMatches = [...g.matches].sort(
      (a, b) => (a.bracketCellMatchIndex ?? 0) - (b.bracketCellMatchIndex ?? 0),
    );

    const reorderPayloads = buildBracketCellReorderPayloads(sortedMatches);
    const createPayloads = sortedMatches
      .filter((m) => typeof m.id === 'string')
      .map((m) => mapBracketCellCreatePayload(m));
    let matchPayloads =
      reorderPayloads.length > 0 || createPayloads.length > 0
        ? [...reorderPayloads, ...createPayloads]
        : [];

    const anchor = sortedMatches[0];
    if (
      !isUnpersisted &&
      !hasCreates &&
      !baseline &&
      !isNewCell &&
      cellCount <= 1 &&
      !isGroupCell &&
      pendingDeletes.length === 0 &&
      matchPayloads.length === 0 &&
      !hasLayoutMetadataPatch(bracketCellPatch) &&
      hasPlaceholderOnAnchor(anchor)
    ) {
      continue;
    }
    const shouldIncludeAnchorMatch =
      !isNewCell &&
      createPayloads.length === 0 &&
      (anchorWouldChange ||
        (!baseline && hasLayoutMetadataPatch(bracketCellPatch)));
    if (
      anchor &&
      shouldIncludeAnchorMatch &&
      !matchPayloads.some((p) => p.id === anchor.id)
    ) {
      matchPayloads = [...matchPayloads, mapBracketCellCreatePayload(anchor)];
    }

    if (
      !isUnpersisted &&
      !hasCreates &&
      matchPayloads.length === 0 &&
      pendingDeletes.length === 0 &&
      !bracketCellPatch
    ) {
      continue;
    }

    const includeBracketCell =
      bracketCellPatch &&
      (isNewCell ||
        isUnpersisted ||
        hasCreates ||
        matchPayloads.length > 0 ||
        pendingDeletes.length > 0 ||
        bracketCellMetadataPatchChanged(bracketCellPatch, baselineBracketCellPatch));

    const patch: TournamentBracketCellClassPlayPatch = {
      id: g.cellId,
      matches: matchPayloads.length > 0 ? matchPayloads : undefined,
      ...(pendingDeletes.length > 0 && { deletedMatchIds: pendingDeletes }),
      ...(includeBracketCell && { bracketCell: bracketCellPatch }),
    };

    if (!patch.matches && !patch.deletedMatchIds && !patch.bracketCell) {
      continue;
    }

    patches.push(patch);
  }

  const seenCellIds = new Set(patches.map((p) => p.id));
  for (const [cellId, cellForm] of Object.entries(values.bracketCells ?? {})) {
    if (seenCellIds.has(cellId)) {
      continue;
    }
    const pendingDeletes = cellForm.pendingDeletedMatchIds ?? [];
    if (pendingDeletes.length === 0) {
      continue;
    }
    const bracketCellPatch = buildBracketCellPatch(
      cellForm,
      cellForm.matchCount ?? 0,
    );
    patches.push({
      id: cellId,
      deletedMatchIds: pendingDeletes,
      ...(bracketCellPatch && { bracketCell: bracketCellPatch }),
    });
  }

  return patches;
}
